import { useHub } from '@/contexts/HubContext';
import type { AppNavGroup } from '@/lib/app-navigation';
import { usePendingAlertsCount } from '../hooks/useAlerts';
import { useNeedsReviewCount, usePspReceiptLinesCount } from '../hooks/useBankStatements';
import { usePendingPayoutsCount } from '../hooks/useFinanceMetrics';
import { apiClientId } from './book';
import { FINANCE_SURFACES, type FinanceAccess, surfaceOpen } from './registry';

/** `enabled` is "this shell is on finance". Surface openness stays in the registry. */
function surfaceQueriesEnabled(id: string, access: FinanceAccess, enabled: boolean): boolean {
  const surface = FINANCE_SURFACES.find((candidate) => candidate.id === id);
  return Boolean(enabled && access.ready && surface && surfaceOpen(surface, access.book, access.wildcard));
}

/** Finance owns its badge identity and data dependencies. */
export function useFinanceNavBadges(groups: AppNavGroup[], access: FinanceAccess, enabled: boolean): AppNavGroup[] {
  const { selectedOperator } = useHub();
  const canSeeBankStatements = surfaceQueriesEnabled('bank-statements', access, enabled);
  const canSeePspMatching = surfaceQueriesEnabled('psp-matching', access, enabled);
  const canSeePayouts = surfaceQueriesEnabled('payouts', access, enabled);
  const clientId = apiClientId(access.book);
  const { data: needsReviewCount } = useNeedsReviewCount(selectedOperator?.id, canSeeBankStatements);
  const { data: pspReceiptCount } = usePspReceiptLinesCount(canSeePspMatching);
  const { data: pendingAlertsCount } = usePendingAlertsCount(enabled, clientId);
  const { data: pendingPayoutsCount } = usePendingPayoutsCount(canSeePayouts, clientId);
  return groups.map((group) => ({
    ...group,
    items: group.items.map((item) => {
      const badge = badgeForSurface(item.id, { needsReviewCount, pspReceiptCount, pendingAlertsCount, pendingPayoutsCount });
      return badge === undefined ? item : { ...item, badge };
    }),
  }));
}

function badgeForSurface(
  surfaceId: string | undefined,
  counts: {
    needsReviewCount: number | null | undefined;
    pspReceiptCount: number | null | undefined;
    pendingAlertsCount: { pending: number; hasMore: boolean } | null | undefined;
    pendingPayoutsCount: number | null | undefined;
  }
): number | string | undefined {
  switch (surfaceId) {
    case 'bank-statements':
      return counts.needsReviewCount ?? undefined;
    case 'psp-matching':
      return counts.pspReceiptCount ?? undefined;
    case 'alerts':
      if (!counts.pendingAlertsCount || counts.pendingAlertsCount.pending <= 0) return undefined;
      return counts.pendingAlertsCount.hasMore ? `${counts.pendingAlertsCount.pending}+` : counts.pendingAlertsCount.pending;
    case 'payouts':
      return counts.pendingPayoutsCount != null && counts.pendingPayoutsCount > 0 ? counts.pendingPayoutsCount : undefined;
    default:
      return undefined;
  }
}
